'use client';

/**
 * AgentApprovalPanel - Human-in-the-loop approval for agent recommendations
 *
 * Shows the selected simulation's recommendation and provides
 * approve/reject controls with feedback capture.
 *
 * @layer presentation
 * @arch_component_type panel
 * @arch_domain agents
 */

import { useState, useEffect } from 'react';
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  Bot,
  Loader2,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils/cn';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { api } from '@/lib/api/client';

interface SimulationJudgment {
  action: string;
  action_type: string;
  confidence: string;
  reasoning: string;
  policy_reference?: string;
  message_draft?: string;
}

interface SimulationDetail {
  simulation_id: string;
  entity_sid: string;
  entity_type: string;
  entity_name: string | null;
  status: string;
  final_judgment: SimulationJudgment | null;
}

const REJECTION_CATEGORIES = [
  { value: 'wrong_context', label: 'Wrong context/data used' },
  { value: 'wrong_rule', label: 'Wrong rule applied' },
  { value: 'wrong_action', label: 'Wrong action recommended' },
  { value: 'timing', label: 'Bad timing' },
  { value: 'other', label: 'Other' },
];

export function AgentApprovalPanel() {
  const [simulation, setSimulation] = useState<SimulationDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectCategory, setRejectCategory] = useState<string>('');
  const [rejectReason, setRejectReason] = useState('');
  const [feedback, setFeedback] = useState<'approved' | 'rejected' | null>(null);

  const selectedNode = useGraphSelectionStore((s) => s.selectedNode);

  // Fetch simulation when selection changes
  useEffect(() => {
    if (selectedNode?.type === 'simulation' && selectedNode.id) {
      fetchSimulation(selectedNode.id);
    } else {
      setSimulation(null);
      setFeedback(null);
    }
  }, [selectedNode?.id, selectedNode?.type]);

  const fetchSimulation = async (simId: string) => {
    setIsLoading(true);
    try {
      const res = await api.getStrategyData<SimulationDetail>('ki_simulation_detail', { simulation_id: simId });
      if (!res.success) throw new Error(res.error || 'ki_simulation_detail failed');
      setSimulation(res.data);
      setFeedback(null);
      setShowRejectForm(false);
    } catch (err) {
      console.error('Failed to fetch simulation:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (execute: boolean = false) => {
    if (!simulation) return;
    setIsSubmitting(true);
    try {
      await api.getStrategyData('ki_simulation_approve', { simulation_id: simulation.simulation_id, execute });
      setFeedback('approved');
    } catch (err) {
      console.error('Failed to approve:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!simulation || !rejectCategory) return;
    setIsSubmitting(true);
    try {
      await api.getStrategyData('ki_simulation_reject', {
        simulation_id: simulation.simulation_id,
        category: rejectCategory,
        reason: rejectReason || 'No reason provided',
      });
      setFeedback('rejected');
      setShowRejectForm(false);
    } catch (err) {
      console.error('Failed to reject:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Empty state
  if (!selectedNode || selectedNode.type !== 'simulation') {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-4">
        <Bot className="w-8 h-8 mb-2 opacity-50" />
        <p className="text-sm">Select a simulation to review</p>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin mb-2" />
        <p className="text-sm">Loading...</p>
      </div>
    );
  }

  if (!simulation?.final_judgment) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-4">
        <AlertTriangle className="w-8 h-8 mb-2 opacity-50" />
        <p className="text-sm">No recommendation available</p>
      </div>
    );
  }

  const judgment = simulation.final_judgment;

  // Already submitted feedback
  if (feedback) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-4">
        {feedback === 'approved' ? (
          <>
            <ThumbsUp className="w-12 h-12 text-green-500 mb-3" />
            <p className="text-lg font-medium">Approved</p>
            <p className="text-sm text-muted-foreground mt-1">
              The recommendation has been recorded
            </p>
          </>
        ) : (
          <>
            <ThumbsDown className="w-12 h-12 text-red-500 mb-3" />
            <p className="text-lg font-medium">Rejected</p>
            <p className="text-sm text-muted-foreground mt-1">
              Feedback recorded for model improvement
            </p>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Recommendation Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Badge className="bg-primary text-primary-foreground">
            {judgment.action}
          </Badge>
          <Badge variant="outline">{judgment.action_type}</Badge>
          <Badge
            className={cn(
              'ml-auto',
              judgment.confidence === 'high'
                ? 'bg-green-100 text-green-800'
                : judgment.confidence === 'medium'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-gray-100 text-gray-800'
            )}
          >
            {judgment.confidence} confidence
          </Badge>
        </div>
      </div>

      {/* Reasoning */}
      <div>
        <p className="text-xs font-medium text-muted-foreground mb-1">Reasoning</p>
        <p className="text-sm">{judgment.reasoning}</p>
      </div>

      {/* Policy Reference */}
      {judgment.policy_reference && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">Policy</p>
          <p className="text-sm text-muted-foreground">{judgment.policy_reference}</p>
        </div>
      )}

      {/* Draft Message Preview */}
      {judgment.message_draft && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">Draft Message</p>
          <div className="bg-muted p-2 rounded-md text-sm whitespace-pre-wrap">
            {judgment.message_draft}
          </div>
        </div>
      )}

      {/* Rejection Form */}
      {showRejectForm ? (
        <div className="space-y-3 border-t pt-4">
          <p className="text-sm font-medium">Why are you rejecting?</p>
          <Select value={rejectCategory} onValueChange={setRejectCategory}>
            <SelectTrigger>
              <SelectValue placeholder="Select reason..." />
            </SelectTrigger>
            <SelectContent>
              {REJECTION_CATEGORIES.map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea
            placeholder="Additional details (optional)..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            className="min-h-[80px]"
          />
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowRejectForm(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleReject}
              disabled={!rejectCategory || isSubmitting}
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Submit Rejection
            </Button>
          </div>
        </div>
      ) : (
        /* Action Buttons */
        <div className="flex gap-2 border-t pt-4">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => setShowRejectForm(true)}
            disabled={isSubmitting}
          >
            <XCircle className="w-4 h-4 mr-1" />
            Reject
          </Button>
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => handleApprove(false)}
            disabled={isSubmitting}
          >
            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            <CheckCircle className="w-4 h-4 mr-1" />
            Approve
          </Button>
          {judgment.action === 'execute' && (
            <Button
              className="flex-1"
              onClick={() => handleApprove(true)}
              disabled={isSubmitting}
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Execute
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
